import {FetchReportResponse, PaginationConfig} from "src/models/reports/FetchReport";
import {TPTaxReportMapResponse} from "src/models/reports/TPTaxReport";
import {DETAIL, FRAMES_CURRENCY_ATTRIBUTE} from "src/utils/constants";

export interface ReportPayload {
    tpTaxReportJSONData: any;
    lastPage: number;
    // Full filtered CLI set (across all pages), sits on the outer payload alongside the paginated
    // tpTaxReportJSONData. Lifted onto reportData in handleFetchReportResponse so the multi-CLI grid
    // can scope the (unpaginated) passthrough / PLI sections to every filtered CLI.
    filteredHeaders?: string[];
}

/**
 * Builds a `CLI -> FRAMES currency` lookup from the report's DETAIL section.
 *
 * The DETAIL section holds one attribute row per header field; the row whose
 * `displayName` is "FRAMES Currency" carries each CLI's currency in its
 * `attributeValue` map (keyed by CLI). Returns an empty map when the report or
 * the FRAMES Currency row is absent.
 */
export const buildCliCurrencyMap = (
    reportData: TPTaxReportMapResponse | null | undefined
): { [cli: string]: string } => {
    const detailRows = reportData?.reportContent?.[DETAIL];
    if (!detailRows) {
        return {};
    }
    const currencyRow = detailRows.find(row => row.displayName === FRAMES_CURRENCY_ATTRIBUTE);
    return currencyRow?.attributeValue ? {...currencyRow.attributeValue} : {};
};

/**
 * Formats a CLI header/label as `<cli> (<currency>)`, e.g. "00008602.0_01_M2-3M (MXN)".
 * Falls back to the bare CLI when no currency is known for it.
 */
export const formatCliWithCurrency = (
    cli: string,
    cliCurrencyMap: { [cli: string]: string }
): string => {
    const currency = cliCurrencyMap[cli];
    return currency ? `${cli} (${currency})` : cli;
};

/**
 * Builds a `CLI -> "<cli> (<currency>)"` label map for the given CLIs using the
 * report's FRAMES currency values. Suitable for the `columnHeaderLabels` prop of
 * TPTaxReportTableGrid and for transforming PLI tab labels.
 *
 * "Total" columns (e.g. "Total Listing Services") carry no FRAMES currency of their
 * own; when at least one CLI has a currency, those Total columns reuse the first
 * available one so they are labelled consistently with the per-CLI columns
 * (e.g. "Total Listing Services (USD)"). Non-total headers without a currency are
 * left untouched.
 */
export const buildCliHeaderLabels = (
    reportData: TPTaxReportMapResponse | null | undefined,
    clis: string[] | undefined
): { [cli: string]: string } => {
    const cliCurrencyMap = buildCliCurrencyMap(reportData);
    // Total columns carry no currency of their own; reuse the first CLI currency present.
    const fallbackCurrency = Object.values(cliCurrencyMap).find(currency => !!currency);
    const labels: { [cli: string]: string } = {};
    (clis ?? []).forEach(header => {
        const currency = cliCurrencyMap[header] || (isTotalColumn(header) ? fallbackCurrency : undefined);
        labels[header] = currency ? `${header} (${currency})` : header;
    });
    return labels;
};

/**
 * True when a report header is a "Total" column (e.g. "Total Listing Services").
 * Total columns have no FRAMES currency of their own and instead reuse a CLI's currency.
 */
const isTotalColumn = (header: string): boolean => /^total\b/i.test(header.trim());


/**
 * Backend field names that differ from the UI model property names.
 * The backend returns these keys in the report JSON; we map them to camelCase UI fields.
 */
const BACKEND_FIELDS = {
    PASSTHROUGH_COST: 'PASSTHROUGH_COST',
    PLI_RECONCILIATION: 'PLI_RECONCILIATION',
    PASSTHROUGH_COST_BY_CLI: 'PASSTHROUGH_COST_BY_CLI',
    CLI_PASSTHROUGH_DETAILS: 'CLI_PASSTHROUGH_DETAILS', // Legacy field name
    PLI_RECONCILIATION_BY_CLI: 'PLI_RECONCILIATION_BY_CLI',
};

/**
 * Handles page change and updates the page configuration
 */
export const handlePageChange = (
    pageNumber: number,
    pageSize: number,
    setPageConfig: (config: PaginationConfig) => void
) => {
    setPageConfig({
        pageNumber,
        pageSize
    });
};

/**
 * Resets the report state to initial values
 */
export const resetReportState = (
    setReportData: (data: any) => void,
    setLastPage: (page: number) => void
) => {
    setReportData(null);
    setLastPage(1);
};

/**
 * Parses the report payload from string to JSON
 */
export const parseReportPayload = (payload: string): ReportPayload => {
    return JSON.parse(payload);
};

/**
 * Validates if the report data contains valid headers
 */
export const isValidReport = (reportData: any): boolean => {
    return reportData?.reportHeaders?.length > 0;
};

/**
 * Maps backend UPPER_SNAKE_CASE field names to camelCase UI model properties.
 * Supports both current and legacy field names for backward compatibility.
 */
export const mapBackendFieldsToUIModel = (reportData: any): void => {
    if (reportData[BACKEND_FIELDS.PASSTHROUGH_COST] && !reportData.passthroughCostDetail) {
        reportData.passthroughCostDetail = reportData[BACKEND_FIELDS.PASSTHROUGH_COST];
    }
    if (reportData[BACKEND_FIELDS.PLI_RECONCILIATION] && !reportData.pliReconciliation) {
        reportData.pliReconciliation = reportData[BACKEND_FIELDS.PLI_RECONCILIATION];
    }
    if (reportData[BACKEND_FIELDS.PASSTHROUGH_COST_BY_CLI] && !reportData.cliPassthroughDetails) {
        reportData.cliPassthroughDetails = reportData[BACKEND_FIELDS.PASSTHROUGH_COST_BY_CLI];
    }
    if (reportData[BACKEND_FIELDS.CLI_PASSTHROUGH_DETAILS] && !reportData.cliPassthroughDetails) {
        reportData.cliPassthroughDetails = reportData[BACKEND_FIELDS.CLI_PASSTHROUGH_DETAILS];
    }
    if (reportData[BACKEND_FIELDS.PLI_RECONCILIATION_BY_CLI] && !reportData.cliPliReconciliation) {
        reportData.cliPliReconciliation = reportData[BACKEND_FIELDS.PLI_RECONCILIATION_BY_CLI];
    }
};

/**
 * Handles the report response and updates the state accordingly.
 * Parses the payload, validates report data, maps backend fields, and updates component state.
 */
export const handleFetchReportResponse = (
    fetchReportResponse: FetchReportResponse | null,
    setReportData: (data: any) => void,
    setLastPage: (page: number) => void,
    showErrorMessage: (message: string) => void
) => {
    if (!fetchReportResponse) {
        return;
    }

    if (!fetchReportResponse.payload) {
        resetReportState(setReportData, setLastPage);
        showErrorMessage(fetchReportResponse.statusMessage);
        return;
    }

    try {
        const payload = parseReportPayload(fetchReportResponse.payload);
        const reportData = payload?.tpTaxReportJSONData;

        if (!reportData || !isValidReport(reportData)) {
            resetReportState(setReportData, setLastPage);
            showErrorMessage("Empty report available, nothing to display");
            return;
        }

        mapBackendFieldsToUIModel(reportData);

        // filteredHeaders lives on the outer payload (not inside tpTaxReportJSONData); lift it onto
        // reportData so the multi-CLI grid can scope the unpaginated passthrough / PLI sections to the
        // full filtered CLI set rather than only the current page's reportHeaders.
        if (payload.filteredHeaders) {
            reportData.filteredHeaders = payload.filteredHeaders;
        }

        setReportData(reportData);
        setLastPage(payload.lastPage);
    } catch (error) {
        resetReportState(setReportData, setLastPage);
        showErrorMessage("Error parsing report data. Contact Engineering Team.");
    }
};
